import React, { FC, useState } from "react";
import { gql } from "graphql-request";
import { Input } from "@src/lib/ui/Input";
import { launchConfetti } from "@src/lib/utils/confetti";
import { useInitializeBillingMutation } from "./InitializeBillingDialog.generated";
import {
  AddressInput,
  GraphqlOperations,
  UserType,
} from "@src/types.generated";
import AddressField from "@src/features/locations/AddressField";
import useUser from "@src/features/app/hooks/useUser";
import Text from "@src/lib/ui/Text";
import PhoneNumberInput from "@src/lib/ui/PhoneNumberInput";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@src/lib/ui/Dialog";
import { Button } from "@src/lib/ui/Button";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export const InitializeBillingDialog: FC<Props> = ({ open, onOpenChange }) => {
  const { user } = useUser();
  const [legalName, setLegalName] = useState(user?.org?.name || "");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState<AddressInput | undefined>();
  const [initializeBilling, { loading }] = useInitializeBillingMutation({
    refetchQueries: [GraphqlOperations.Query.BillingBanner],
    onCompleted: (data) => {
      if (data.initializeBilling.success) {
        onOpenChange(false);
        launchConfetti();
      }
    },
  });
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState<string | undefined>(undefined);
  const [jobTitle, setJobTitle] = useState("");
  const isValid = legalName && email && address;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[600px]">
        <DialogHeader>
          <DialogTitle>Initialize Billing</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center gap-3">
          <Text variant="fine-print-title">Company Info</Text>
          <Input
            label="Legal name"
            value={legalName}
            onChange={(e) => setLegalName(e.target.value)}
          />
          <AddressField inputLabel="Address" onAddressChange={setAddress} />
          <Text variant="fine-print-title" marginTop={12}>
            Billing Contact Info
          </Text>
          <div className="flex gap-3 self-stretch">
            <Input
              label="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
            <Input
              label="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>
          <div className="flex gap-3 self-stretch">
            <Input
              label="Billing Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Input
              label="Job Title"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
            />
          </div>
          <PhoneNumberInput
            label="Phone Number"
            value={phoneNumber}
            onChange={(v) => setPhoneNumber(v)}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!isValid}
            loading={loading}
            onClick={() => {
              if (!isValid) {
                return;
              }
              initializeBilling({
                variables: {
                  input: {
                    legalName,
                    email: email || "",
                    address,
                    billingContact: phoneNumber
                      ? {
                          firstName,
                          lastName,
                          phoneNumber,
                          language: "en",
                          jobTitle,
                          locationIds: [],
                          roleIds: [],
                          userType: UserType.Admin,
                          email,
                        }
                      : undefined,
                  },
                },
              });
            }}
          >
            Initialize
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

gql`
  mutation InitializeBilling($input: InitializeBillingInput!) {
    initializeBilling(input: $input) {
      success
    }
  }
`;
